import {XdSentry} from "../../libs/sentry/xd-sentry";
import {isDevXd}  from "../../ui-components/debug/is-dev";
import {snv}      from "../../utils/arr-path";

import {addImageReferenceToProductTags} from "./add-image-reference-to-product-tags";

export const importImages = async ({ccEditor, surfaceData}) => {
    try {

        let importPayload:any = {}; // will hold all data to send to editor.loadUserInfo
        let removeItems = []; // holds a list of image areas where each image should be removed

        let product = await ccEditor.getProduct();
        for (const surface of surfaceData) {
            for (const item of surface.items) {

                // apply changes if anything changed
                if (item.image?.httpPath !== item.originalImage?.httpPath) {

                    // if there is an image reference apply to the image area
                    if (item.image) {
                        importPayload = snv(importPayload, ["surfaces", surface.name, item.name], item.image.httpPathPrint);
                        // if (!importPayload) importPayload = {surfaces: {}};
                        // if (!importPayload.surfaces[surface.name]) importPayload.surfaces[surface.name] = {};
                        // importPayload.surfaces[surface.name][item.name] = item.image.httpPathPrint;

                        // add the image ID to product tags
                        await addImageReferenceToProductTags (product, "order", item.image.imageId, surface.name, item.name);

                    } else {
                        // if no longer an image reference, queue removal from the image area
                        removeItems.push({
                            surface: item.surfaceName,
                            name: item.name,
                            id: item.id
                        });
                    }

                }

            }
        }

        // issue error if nothing to be done - apply import should not have been enabled
        if (!importPayload.surfaces && !removeItems.length) throw "Import requested with no changes";

        // apply all imports to the editor
        if (importPayload) {
            await ccEditor.callEditorMethod("loadUserInfo", importPayload);
        }

        // apply all removals to the editor - removals cannot be applied in the same way as additions and
        // instead need direct interaction with the current surface
        if (removeItems) {

            // refresh the product with the current state of the editor

            // find the current surface
            const originalSurface = product.currentSurface.name;
            let lastSurface = originalSurface;
            let removedItemOnOriginalSurface = false;

            // loop through all items and apply against the product
            for (const removal of removeItems) {

                // check if the removed item is on a different surface
                const removedItemOnDifferentSurface = (lastSurface !== removal.surface);

                // if the item was removed from another surface, switch to it
                if (removedItemOnDifferentSurface) {
                    product = await product.switchTo(product.surfaces.find(x => x.name === removal.surface));
                }

                // remove the content for the ID
                let productItem = await product.getItemById(removal.id);
                productItem._content = null;
                await product.setItem(productItem);

                // remove the image reference from product tags
                await removeImageReferenceFromProductTags(product, removal.surface, removal.name);

                // if an item was removed from the original surface, then mark as such to trigger display
                // refresh logic at the end of all image removal operations
                if (removal.surface === originalSurface) {
                    removedItemOnOriginalSurface = true;
                }

                // record the previous surface to see if a switch is required on next iteration
                lastSurface = removal.surface;

            }

            // return to the original surface if changed
            if (lastSurface !== originalSurface) {
                product = await product.switchTo(product.surfaces.find(x => x.name === originalSurface));
            }

            // if any items removed from the current surface, then need to cycle selections to trigger the
            // display of the image to change, and then re-add the handlers for the image selection buttons
            if (removedItemOnOriginalSurface) {
                ccEditor.callEditorMethod("selectAllItems");
                ccEditor.callEditorMethod("clearSelection");
                ccEditor.addEditorSelectImageButtonHandlers();
            }

        }

    } catch (e) {

        XdSentry.addBreadcrumb({
            category: "custcanv",
            message: "Image import failed.",
            level: XdSentry.Severity.Error,
            data: e
        });
        XdSentry.captureMessage("Image Import Error", XdSentry.Severity.Error);

        if (isDevXd()) console.log("CcEditor.importImages failed import", e);

        return "There was an error importing your images.";

    }

    return "";

};


const removeImageReferenceFromProductTags = async (product, surfaceName, variableName) => {
    if (isDevXd()) console.log("CcEditor.removeImageReferenceFromProductTags removing image ID from product tags", surfaceName, variableName);

    // load tags
    let tags = await product.getTags();

    // return if no image sources
    const imageSource = tags?.imageSources?.find(x => x.surfaceName === surfaceName && x.variableName === variableName);
    if (!imageSource) return;

    // remove the image source from tags
    tags.imageSources.splice(tags.imageSources.indexOf(imageSource), 1);
    if (isDevXd()) console.log("CcEditor.removeImageReferenceFromProductTags writing product tags", tags);

    // save the product tags
    await product.setTags(tags);

};

